
import mapFunctions from '@/mixins/mapFunctions'
import isEqual from 'lodash/isEqual'

export default {
    mixins: [mapFunctions],
    props: {
        value: {
            type: null,
            default: null,
        },
        placeholder: {
            type: [String, Object],
            default: '',
        },
        hint: {
            type: String,
            default: undefined,
        },
        help: {
            type: String,
            default: undefined,
        },
        spacing: {
            type: String,
            default: 'gap-2 mt-2',
        },
        disabled: Boolean,
        isAddress: Boolean,
        isObject: Boolean,
        hideName: Boolean,
        confirmAddress: Boolean,
        showCountry: Boolean,
    },

    data() {
        return {
            address: {},
            autocompleteOptions: {},
            isInited: false,
        }
    },
    computed: {
        id() {
            return `location-${this._uid}`
        },
        placeholders() {
            if (!this.isAddress) return this.placeholder

            return {
                ...{
                    full_address: 'Type your address',
                    address: 'Street Address',
                    address_2: 'Apt, Suite, Unit (optional)',
                    city: 'City',
                    state: 'State',
                    zip: 'Zip',
                    firstname: 'First name',
                    lastname: 'Last name',
                    country: 'Country',
                },
                ...this.placeholder,
            }
        },
        autocompletePlaceholder() {
            return this.isAddress ? this.placeholders.full_address : this.placeholders
        },
        autocompleteValue() {
            return this.isAddress ? this.address.full_address : this.value
        },
    },

    mounted() {
        if (this.isAddress) {
            this.$set(this, 'address', {
                full_address: null,
                firstname: null,
                lastname: null,
                address: null,
                address_2: null,
                city: null,
                state: null,
                zip: null,
                country: null,
            })
            this.$watch(
                'value',
                (value, valueBefore) => {
                    if (!isEqual(value, this.address)) this.parseAddress()
                },
                { immediate: true }
            )
            this.$watch(
                'address',
                () => {
                    if (this.address.full_address)
                        this.$emit(
                            'valid',
                            !!(
                                this.address.full_address &&
                                this.address.address &&
                                this.address.city &&
                                this.address.state &&
                                this.address.zip &&
                                this.address.country
                            )
                        )
                },
                { immediate: true, deep: true }
            )
        }
    },

    methods: {
        placeChanged(val) {
            if (this.isAddress) this.getAddressObject(val)
            else {
                this.$emit('change', val)
                this.$emit('input', this.isObject ? val : val.formatted_address)
            }
        },

        setField(key, value, burst = false) {
            this.$set(this.address, key, value)
            if (!burst) {
                this.$nextTick(() => {
                    this.$emit('input', this.isObject ? this.address : JSON.stringify(this.address))
                })
            }
        },

        getAddressObject(val) {
            this.setField('full_address', val.formatted_address, true)
            this.setField('state', this.getAddressComponent(val.address_components, 'administrative_area_level_1', 'short_name'), true)
            this.setField('country', this.getAddressComponent(val.address_components, 'country', 'long_name'), true)
            this.setField('zip', this.getAddressComponent(val.address_components, 'postal_code', 'long_name'), true)
            this.setField('city', this.getAddressComponent(val.address_components, 'locality', 'long_name'), true)

            const route = this.getAddressComponent(val.address_components, 'route', 'short_name')
            const streetNumber = this.getAddressComponent(val.address_components, 'street_number', 'long_name')
            this.setField('address', route && streetNumber ? `${streetNumber} ${route}` : '')
        },

        blurHandler(e) {
            if (this.isAddress)
                if (e.target?.value) this.setField('full_address', e.target.value, true)
                else this.$emit('blur')
        },

        parseAddress() {
            if (this.value) {
                if (this.value.constructor === Object) {
                    this.address = {
                        full_address: this.value.full_address,
                        firstname: this.value.first_name,
                        lastname: this.value.last_name,
                        address: this.value.address,
                        address_2: this.value.address_2,
                        city: this.value.city,
                        state: this.value.state,
                        zip: this.value.zip,
                        country: this.value.country,
                    }
                    return
                }

                try {
                    const obj = JSON.parse(this.value)
                    this.address =
                        obj.constructor === Object
                            ? obj
                            : {
                                  full_address: null,
                                  firstname: null,
                                  lastname: null,
                                  address: null,
                                  address_2: null,
                                  city: null,
                                  state: null,
                                  zip: null,
                                  country: null,
                              }
                } catch (error) {
                    this.address.full_address = this.value.address || this.value
                }
            }
        },
    },
}
